import { green, red } from '@mui/material/colors';
import { Marker, Popup } from 'maplibre-gl';
import React, { useRef } from 'react';
import { createRoot } from 'react-dom/client';
import ClosedContributionIcon from '../../assets/images/report/contribution-closed-icon.svg';
import OpenContributionIcon from '../../assets/images/report/contribution-open-icon.svg';
import { ReportPopup } from '../../components/map/report-popup';
import { useLayers } from './layers';
import { usePopup } from './popup';
import { useSource } from './source';
const sourceId = 'reports';
export const reportsLayerId = 'reports';
const asMarkerMinZoom = 14;
export function useReports(map, theme, {
  onDetailsClick
} = {}) {
  const initialized = useRef(false);
  const markers = useRef({});
  const selectedReportId = useRef(null);
  const {
    addGeoJSONSource,
    updateGeoJSONSource,
    clearGeoJSONSource
  } = useSource(map, sourceId);
  const {
    addCircleLayer
  } = useLayers(map);
  const {
    centerPopup
  } = usePopup(map);
  function init() {
    if (!map || initialized.current) return;
    addGeoJSONSource();
    addCircleLayer(reportsLayerId, sourceId, {
      'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 10, 5],
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 1,
      'circle-color': ['get', 'color'],
      'circle-stroke-color': '#fff'
    });
    map.on('mouseenter', reportsLayerId, event => {
      if (event.defaultPrevented) return;
      event.originalEvent.preventDefault();
      event.preventDefault();
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', reportsLayerId, () => {
      map.getCanvas().style.cursor = '';
    });
    map.on('click', reportsLayerId, event => {
      if (event.originalEvent.defaultPrevented) return;
      event.originalEvent.preventDefault();
      const {
        features
      } = event;
      if (features && features.length > 0) {
        const {
          id,
          geometry
        } = features[0];
        if (typeof id === 'number') selectedReportId.current = id;
        if (geometry.type === 'Point') {
          const {
            coordinates: [lng, lat]
          } = geometry;
          map.flyTo({
            center: {
              lat,
              lng
            },
            zoom: asMarkerMinZoom
          });
        }
      }
    });
    initialized.current = true;
  }
  function openReportPopup(report) {
    closePrevSelectedPoiPopup();
    const {
      id,
      geoPoint: {
        coordinates: [lng, lat]
      }
    } = report;
    const selectedMarker = markers.current[id];
    if (!selectedMarker || selectedMarker.marker.getPopup().isOpen()) return;
    selectedMarker.marker.togglePopup();
    const ele = document.getElementById(`report-${report.id}-popup`);
    if (ele) {
      const container = createRoot(ele);
      container.render(/*#__PURE__*/React.createElement(ReportPopup, {
        onClose: () => {
          if (selectedMarker.marker.getPopup().isOpen()) selectedMarker.marker.togglePopup();
          selectedReportId.current = null;
          if (container) container.unmount();
        },
        onDetailsClick: onDetailsClick ? () => onDetailsClick(id) : undefined,
        report: report,
        sourcesMap: undefined,
        theme: theme
      }));
    }
    centerPopup({
      lat,
      lng
    }, selectedMarker.marker.getPopup(), {
      height: 200
    });
  }
  function closePrevSelectedPoiPopup() {
    Object.values(markers.current).forEach(({
      id,
      marker
    }) => {
      if (id !== selectedReportId.current && marker.getPopup().isOpen()) marker.togglePopup();
    });
  }
  function addMarker(report, {
    selectable
  } = {}) {
    if (!map || markers.current[report.id]) return;
    const {
      id,
      geoPoint: {
        coordinates: [lng, lat]
      },
      status
    } = report;
    const element = document.createElement('div');
    element.style.border = '2px solid #fff';
    element.style.borderRadius = '50%';
    element.style.backgroundImage = status === 'OPEN' ? `url(${OpenContributionIcon})` : `url(${ClosedContributionIcon})`;
    element.style.backgroundPosition = 'center';
    element.style.backgroundRepeat = 'no-repeat';
    element.style.backgroundSize = '20px';
    element.style.width = '16px';
    element.style.height = '16px';
    element.style.cursor = selectable ? 'pointer' : 'auto';
    element.addEventListener('click', event => {
      event.stopPropagation();
      if (selectable && report) {
        selectedReportId.current = report.id;
        openReportPopup(report);
      }
    });
    const marker = new Marker({
      element
    }).setLngLat({
      lat,
      lng
    }).setPopup(new Popup({
      maxWidth: '350px',
      closeButton: false,
      closeOnClick: false,
      className: 'map-custom-popup',
      offset: 6
    }).setHTML(`<div id="report-${id}-popup"></div>`)).addTo(map);
    if (id) markers.current[id] = {
      id,
      marker
    };
  }
  function update(reports) {
    if (!map) return;
    if (map.getZoom() >= asMarkerMinZoom) {
      updateGeoJSONSource({
        type: 'FeatureCollection',
        features: []
      });
      reports.forEach(report => addMarker(report, {
        selectable: true
      }));
      const selectedReport = reports.find(({
        id
      }) => id === selectedReportId.current);
      if (selectedReport) openReportPopup(selectedReport);
    } else {
      Object.values(markers.current).forEach(({
        id,
        marker
      }) => {
        marker.remove();
        delete markers.current[id];
      });
      selectedReportId.current = null;
      updateGeoJSONSource({
        type: 'FeatureCollection',
        features: reports.map(({
          id,
          geoPoint,
          status
        }) => {
          return {
            id,
            type: 'Feature',
            geometry: geoPoint,
            properties: {
              color: status === 'OPEN' ? red[500] : green[500]
            }
          };
        })
      });
    }
  }
  function select(report) {
    if (!map) return;
    const {
      geoPoint: {
        coordinates: [lng, lat]
      }
    } = report;
    addMarker(report);
    map.flyTo({
      center: {
        lat,
        lng
      },
      zoom: 14
    });
  }
  function clear() {
    Object.values(markers.current).forEach(({
      id,
      marker
    }) => {
      marker.remove();
      delete markers.current[id];
    });
    selectedReportId.current = null;
    clearGeoJSONSource();
  }
  return {
    initialized: initialized.current,
    init,
    update,
    select,
    clear
  };
}